import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Plus, Edit, Trash2, Loader2, ArrowLeft, Save, X, User, Globe, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { adminTeamAPI } from '../../services/adminApi';
import ImageUploader from '../../components/ImageUploader';
import { useLanguage } from '../../contexts/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${API_BASE_URL}${url}`;
  return `${API_BASE_URL}/api/uploads/image/${url}`;
};

// List Component
export const AdminTeam = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(null);
  const { t } = useLanguage();

  const fetchMembers = async () => {
    try {
      const data = await adminTeamAPI.getAll();
      setMembers(data);
    } catch (err) {
      toast.error(t('admin.toast.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.common.confirmDelete'))) return;
    
    try {
      await adminTeamAPI.delete(id);
      toast.success(t('admin.toast.deleted'));
      fetchMembers();
    } catch (err) {
      toast.error(t('admin.toast.error'));
    }
  };

  const handleReorder = async (id, direction) => {
    setReordering(id);
    try {
      await adminTeamAPI.reorder(id, direction);
      await fetchMembers();
    } catch (err) {
      toast.error(t('admin.toast.error'));
    } finally {
      setReordering(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  return (
    <div data-testid="admin-team">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ color: '#F8FAFC' }}>{t('admin.team.title')}</h1>
        <Link
          to="/admin/team/new"
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
          style={{
            background: 'linear-gradient(90deg, #00F0FF 0%, #0F172A 100%)',
            color: '#F8FAFC',
            border: '2px solid #00F0FF',
          }}
        >
          <Plus size={18} />
          {t('admin.team.newMember')}
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {members.map((member, index) => (
          <div key={member.id} className="glass-card p-6">
            {/* Reorder buttons */}
            <div className="flex justify-end gap-1 mb-3">
              <button
                onClick={() => handleReorder(member.id, 'up')}
                disabled={index === 0 || reordering === member.id}
                className="p-1.5 rounded transition-colors disabled:opacity-30"
                style={{ color: '#00F0FF', background: 'rgba(0, 240, 255, 0.1)' }}
                data-testid={`reorder-up-${member.id}`}
              >
                <ChevronUp size={16} />
              </button>
              <button
                onClick={() => handleReorder(member.id, 'down')}
                disabled={index === members.length - 1 || reordering === member.id}
                className="p-1.5 rounded transition-colors disabled:opacity-30"
                style={{ color: '#00F0FF', background: 'rgba(0, 240, 255, 0.1)' }}
                data-testid={`reorder-down-${member.id}`}
              >
                <ChevronDown size={16} />
              </button>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0" style={{
                background: 'rgba(0, 240, 255, 0.2)'
              }}>
                {member.image ? (
                  <img 
                    src={getImageUrl(member.image)} 
                    alt={member.name} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00F0FF" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg></div>';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User size={32} style={{ color: '#00F0FF' }} />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold truncate" style={{ color: '#F8FAFC' }}>{member.name}</h3>
                <p className="text-sm" style={{ color: '#00F0FF' }}>{member.role}</p>
                <p className="text-sm mt-2 line-clamp-2" style={{ color: '#94A3B8' }}>{member.bio}</p>
                {/* Language indicators */}
                <div className="flex items-center gap-1 mt-2">
                  <span className="px-2 py-0.5 text-xs rounded" style={{ background: 'rgba(0, 240, 255, 0.2)', color: '#00F0FF' }}>EN</span>
                  {member.role_tr && (
                    <span className="px-2 py-0.5 text-xs rounded" style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#EF4444' }}>TR</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-4 pt-4" style={{ borderTop: '1px solid rgba(0, 240, 255, 0.1)' }}>
              <Link
                to={`/admin/team/${member.id}`}
                className="p-2 rounded-lg transition-colors"
                style={{ color: '#00F0FF', background: 'rgba(0, 240, 255, 0.1)' }}
              >
                <Edit size={16} />
              </Link>
              <button
                onClick={() => handleDelete(member.id)}
                className="p-2 rounded-lg transition-colors"
                style={{ color: '#EF4444', background: 'rgba(239, 68, 68, 0.1)' }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {members.length === 0 && (
        <div className="glass-card p-8 text-center" style={{ color: '#94A3B8' }}>
          {t('admin.team.noMembers')}
        </div>
      )}
    </div>
  );
};

// Form Component with Language Tabs
export const AdminTeamForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('en');
  const [formData, setFormData] = useState({
    name: '',
    // English
    role: '',
    bio: '',
    // Turkish
    role_tr: '',
    bio_tr: '',
    // Common
    image: '',
    linkedin: ''
  });

  useEffect(() => {
    if (isEdit) {
      const fetchMember = async () => {
        try {
          const data = await adminTeamAPI.getById(id);
          setFormData({
            name: data.name || '',
            role: data.role || '',
            bio: data.bio || '',
            role_tr: data.role_tr || '',
            bio_tr: data.bio_tr || '',
            image: data.image || '',
            linkedin: data.linkedin || ''
          });
        } catch (err) {
          toast.error(t('admin.toast.error'));
          navigate('/admin/team');
        } finally {
          setLoading(false);
        }
      };
      fetchMember();
    }
  }, [id, isEdit, navigate, t]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (url) => {
    setFormData({ ...formData, image: url });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit) {
        await adminTeamAPI.update(id, formData);
        toast.success(t('admin.toast.updated'));
      } else {
        await adminTeamAPI.create(formData);
        toast.success(t('admin.toast.saved'));
      }
      navigate('/admin/team');
    } catch (err) {
      toast.error(t('admin.toast.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  return (
    <div data-testid="admin-team-form">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/team')}
          className="p-2 rounded-lg"
          style={{ color: '#94A3B8', background: 'rgba(148, 163, 184, 0.1)' }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold" style={{ color: '#F8FAFC' }}>
          {isEdit ? t('admin.team.editMember') : t('admin.team.newMember')}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="glass-card p-6 space-y-6">
        {/* Common Fields - Image, Name, LinkedIn */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <ImageUploader
              value={formData.image}
              onChange={handleImageChange}
              label={t('admin.team.form.profilePhoto')}
              placeholder={t('admin.team.form.uploadPhoto')}
              aspectRatio="1/1"
            />
          </div>

          <div className="md:col-span-2 space-y-6">
            <div>
              <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>{t('admin.team.form.fullName')} *</label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  color: '#F8FAFC'
                }}
              />
            </div>

            <div>
              <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>{t('admin.team.form.linkedin')}</label>
              <input
                type="url"
                name="linkedin"
                value={formData.linkedin}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  color: '#F8FAFC'
                }}
                placeholder="https://linkedin.com/in/..."
              />
            </div>
          </div>
        </div>

        {/* Language Tabs */}
        <div className="flex items-center gap-2 p-1 rounded-lg" style={{ background: 'rgba(15, 23, 42, 0.6)' }}>
          <button
            type="button"
            onClick={() => setActiveTab('en')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
            style={{
              background: activeTab === 'en' ? 'rgba(0, 240, 255, 0.2)' : 'transparent',
              color: activeTab === 'en' ? '#00F0FF' : '#94A3B8',
              border: activeTab === 'en' ? '1px solid #00F0FF' : '1px solid transparent'
            }}
          >
            <Globe size={16} />
            {t('admin.common.english')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('tr')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
            style={{
              background: activeTab === 'tr' ? 'rgba(239, 68, 68, 0.2)' : 'transparent',
              color: activeTab === 'tr' ? '#EF4444' : '#94A3B8',
              border: activeTab === 'tr' ? '1px solid #EF4444' : '1px solid transparent'
            }}
          >
            <Globe size={16} />
            {t('admin.common.turkish')}
          </button>
        </div>

        {/* English Fields */}
        {activeTab === 'en' && (
          <div className="space-y-6" data-testid="en-fields">
            <div>
              <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>{t('admin.team.form.roleEn')} *</label>
              <input
                type="text"
                name="role"
                required
                value={formData.role}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  color: '#F8FAFC'
                }}
                placeholder="Team Captain, Software Lead..."
              />
            </div>

            <div>
              <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>{t('admin.team.form.bioEn')} *</label>
              <textarea
                name="bio"
                required
                rows={4}
                value={formData.bio}
                onChange={handleChange}
                className="w-full px-4 py-3 resize-none rounded-lg"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  color: '#F8FAFC'
                }}
                placeholder="Biography in English..."
              />
            </div>
          </div>
        )}

        {/* Turkish Fields */}
        {activeTab === 'tr' && (
          <div className="space-y-6" data-testid="tr-fields">
            <div className="p-4 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
              <p className="text-sm" style={{ color: '#F8FAFC' }}>
                💡 {t('admin.buildLogs.form.trNote')}
              </p>
            </div>
            
            <div>
              <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>{t('admin.team.form.roleTr')}</label>
              <input
                type="text"
                name="role_tr"
                value={formData.role_tr}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#F8FAFC'
                }}
                placeholder="Takım Kaptanı, Yazılım Lideri..."
              />
            </div>

            <div>
              <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>{t('admin.team.form.bioTr')}</label>
              <textarea
                name="bio_tr"
                rows={4}
                value={formData.bio_tr}
                onChange={handleChange}
                className="w-full px-4 py-3 resize-none rounded-lg"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#F8FAFC'
                }}
                placeholder="Türkçe biyografi..."
              />
            </div>
          </div>
        )}

        <div className="flex items-center gap-4 pt-4" style={{ borderTop: '1px solid rgba(0, 240, 255, 0.1)' }}>
          <button
            type="button"
            onClick={() => navigate('/admin/team')}
            className="px-6 py-3 rounded-lg"
            style={{ color: '#94A3B8', background: 'rgba(148, 163, 184, 0.1)' }}
          >
            <X size={18} className="inline mr-2" />
            {t('admin.common.cancel')}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 rounded-lg flex items-center gap-2"
            style={{
              background: 'linear-gradient(90deg, #00F0FF 0%, #0F172A 100%)',
              color: '#F8FAFC',
              border: '2px solid #00F0FF',
            }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save size={18} />}
            {isEdit ? t('admin.common.update') : t('admin.common.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminTeam;
